/**
 * Auth login / register / GET /auth/user/ and GET /user/user/ (basic and full):
 * - flags: `true` / `false` when the related value exists, `null` when it does not
 * - membership_type / membership_status: integer PK, or `null` when there is no row
 *
 * Never test these with truthiness. `null` is absent; `false` is present and not verified.
 * `invite_name` / `invite_description` still use `""` — those are labels, not flags.
 */

export function isAuthFlagTrue(value: unknown): boolean {
  return value === true;
}

export function isAuthFlagFalse(value: unknown): boolean {
  return value === false;
}

/** No address / no invite / no membership row. */
export function isAuthFlagNull(value: unknown): boolean {
  return value === null;
}

export function isNonEmptyAuthString(value: unknown): boolean {
  return typeof value === 'string' && value !== '';
}

export function hasAuthEmail(user: { email?: unknown; email_pending?: unknown } | null | undefined): boolean {
  return isNonEmptyAuthString(user?.email) || isNonEmptyAuthString(user?.email_pending);
}

export function hasAuthPhone(
  user: { phone_number?: unknown; phone_number_pending?: unknown } | null | undefined
): boolean {
  return isNonEmptyAuthString(user?.phone_number) || isNonEmptyAuthString(user?.phone_number_pending);
}

export function isAuthEmailVerified(user: { is_email_verified?: unknown } | null | undefined): boolean {
  return isAuthFlagTrue(user?.is_email_verified);
}

export function isAuthPhoneVerified(user: { is_phone_number_verified?: unknown } | null | undefined): boolean {
  return isAuthFlagTrue(user?.is_phone_number_verified);
}

/** Canonical `is_email_pending_verified` or login alias `is_pending_email_verified`. */
export function isAuthEmailPendingVerified(user: {
  is_email_pending_verified?: unknown;
  is_pending_email_verified?: unknown;
} | null | undefined): boolean {
  return isAuthFlagTrue(user?.is_email_pending_verified) || isAuthFlagTrue(user?.is_pending_email_verified);
}

/** Canonical `is_phone_number_pending_verified` or login alias `is_pending_phone_number_verified`. */
export function isAuthPhonePendingVerified(user: {
  is_phone_number_pending_verified?: unknown;
  is_pending_phone_number_verified?: unknown;
} | null | undefined): boolean {
  return (
    isAuthFlagTrue(user?.is_phone_number_pending_verified) || isAuthFlagTrue(user?.is_pending_phone_number_verified)
  );
}

export function hasAuthEmailBypass(user: {
  invite_bypass_email_verify?: unknown;
  signup_invite_code?: { bypass_email_verify?: unknown };
} | null | undefined): boolean {
  return (
    isAuthFlagTrue(user?.invite_bypass_email_verify) || isAuthFlagTrue(user?.signup_invite_code?.bypass_email_verify)
  );
}

export function hasAuthPhoneBypass(user: {
  invite_bypass_phone_verify?: unknown;
  signup_invite_code?: { bypass_phone_verify?: unknown };
} | null | undefined): boolean {
  return (
    isAuthFlagTrue(user?.invite_bypass_phone_verify) || isAuthFlagTrue(user?.signup_invite_code?.bypass_phone_verify)
  );
}

/** `null` means no invite — do not skip onboarding. */
export function hasAuthOnboardingBypass(user: {
  invite_bypass_onboarding?: unknown;
  signup_invite_code?: { bypass_onboarding?: unknown };
} | null | undefined): boolean {
  return isAuthFlagTrue(user?.invite_bypass_onboarding) || isAuthFlagTrue(user?.signup_invite_code?.bypass_onboarding);
}

/** `null` means no invite — do not skip the CV / LinkedIn gate. */
export function hasAuthCvLinkedinBypass(user: {
  invite_bypass_cv_linkedin?: unknown;
  signup_invite_code?: { bypass_cv_linkedin?: unknown };
} | null | undefined): boolean {
  return (
    isAuthFlagTrue(user?.invite_bypass_cv_linkedin) || isAuthFlagTrue(user?.signup_invite_code?.bypass_cv_linkedin)
  );
}

/**
 * Integer PK from auth/profile. `null` means no row.
 * Do not coerce with `Number()` — `Number(null)` is `0`.
 */
export function coerceAuthTypeFk(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  return undefined;
}
